import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { menuItemStyles } from '@utils/styles';
import {
  DataAccessProvider,
  MMP_SOURCE_MAP,
  SelfServeDataAccessStatus,
  SelfServeDataAccessStatusMap,
} from '@constants/constants';
import DataAccessSourceFields from '../../../components/DataAccessSourceFields';
import { UpdateSelfServeRequestStatusParams } from '../../types';

const DATA_ACCESS_SOURCE_OPTIONS = Object.entries(MMP_SOURCE_MAP)
  .filter(([value]) => value !== DataAccessProvider.NONE)
  .map(([value, label]) => ({
    label,
    value,
  }));

const SelfServeRequestStatus = Object.entries(SelfServeDataAccessStatusMap).map(
  ([value, label]) => ({
    label,
    value,
  }),
);

const dataSourcesWithNoValidation = new Set([
  DataAccessProvider.ADJUST,
  DataAccessProvider.APPS_FLYER,
  DataAccessProvider.GOOGLE_DRIVE,
]);

const statusTransitionMap: Record<string, SelfServeDataAccessStatus[]> = {
  [SelfServeDataAccessStatus.PENDING_USER_CREATION]: [
    SelfServeDataAccessStatus.PENDING_USER_CREATION,
  ],
  [SelfServeDataAccessStatus.PENDING_NDA_CREATION]: [
    SelfServeDataAccessStatus.PENDING_NDA_CREATION,
  ],
  [SelfServeDataAccessStatus.READY_TO_SEND_NDA]: [SelfServeDataAccessStatus.READY_TO_SEND_NDA],
  [SelfServeDataAccessStatus.PENDING_NDA_SIGNATURE]: [
    SelfServeDataAccessStatus.PENDING_NDA_SIGNATURE,
  ],
  [SelfServeDataAccessStatus.PENDING_ACCESS]: [
    SelfServeDataAccessStatus.PENDING_ACCESS,
    SelfServeDataAccessStatus.CALL_SCHEDULED,
    SelfServeDataAccessStatus.ACCESS_GRANTED,
  ],
  [SelfServeDataAccessStatus.CALL_SCHEDULED]: [
    SelfServeDataAccessStatus.CALL_SCHEDULED,
    SelfServeDataAccessStatus.ACCESS_GRANTED,
  ],
  [SelfServeDataAccessStatus.ACCESS_GRANTED]: [
    SelfServeDataAccessStatus.ACCESS_GRANTED,
    SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL,
  ],
  [SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL]: [
    SelfServeDataAccessStatus.DATA_FETCH_SUCCESSFUL,
  ],
  [SelfServeDataAccessStatus.DATA_FETCH_FAILED]: [SelfServeDataAccessStatus.DATA_FETCH_FAILED],
  [SelfServeDataAccessStatus.DATA_FETCH_PARTIAL]: [SelfServeDataAccessStatus.DATA_FETCH_PARTIAL],
  [SelfServeDataAccessStatus.DASHBOARD_CREATION_FAILED]: [
    SelfServeDataAccessStatus.DASHBOARD_CREATION_FAILED,
  ],
  [SelfServeDataAccessStatus.PUBLISHED]: [
    SelfServeDataAccessStatus.PUBLISHED,
    SelfServeDataAccessStatus.REOPEN,
  ],
  [SelfServeDataAccessStatus.REOPEN]: [SelfServeDataAccessStatus.REOPEN],
};

const getDropdownOptions = (status: string) => {
  const allowedStatuses = statusTransitionMap[status] || [];
  return SelfServeRequestStatus.filter((option) =>
    allowedStatuses.includes(option.value as SelfServeDataAccessStatus),
  );
};

const isAdminAllowedToModifyRequestStatus = (dataSource: DataAccessProvider) => {
  // this function will return true if the admin is allowed to modify the status
  // it will return false if the admin is not allowed to modify the status
  // admin allowed to modify request status if the data source is not APPS_FLYER or ADJUST
  return dataSource !== DataAccessProvider.APPS_FLYER && dataSource !== DataAccessProvider.ADJUST;
};

interface RequestStatusSelectorProps {
  requestStatus: string | null;
  updateSelfServeRequestStatus: (params: UpdateSelfServeRequestStatusParams) => void;
  mmp: string | null;
  isUpdatingStatus: boolean;
}

interface FormData {
  accountName: string;
  iapMargin: string;
  iaaIdList: string[];
  connectionDetails: any | null;
}

const RequestStatusSelector: FC<RequestStatusSelectorProps> = ({
  requestStatus,
  updateSelfServeRequestStatus,
  mmp,
  isUpdatingStatus,
}) => {
  const [status, setStatus] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [dataAccessSource, setDataAccessSource] = useState<DataAccessProvider | null>(null);
  const [formData, setFormData] = useState<FormData>({
    accountName: '',
    iapMargin: '',
    iaaIdList: [],
    connectionDetails: null,
  });

  // Reset form state when status changes
  useEffect(() => {
    setStatus(requestStatus || '');
    if (requestStatus !== SelfServeDataAccessStatus.ACCESS_GRANTED) {
      resetFormState();
    }
  }, [requestStatus]);

  // Reset form state when dataAccessSource changes
  useEffect(() => {
    if (dataAccessSource) {
      const isDataSourceNotRequireValidation = dataSourcesWithNoValidation.has(dataAccessSource);
      resetFormState(isDataSourceNotRequireValidation);
    } else {
      resetFormState();
    }
  }, [dataAccessSource]);

  const resetFormState = (isFormValid: boolean = false) => {
    setFormData({
      accountName: '',
      iapMargin: '',
      iaaIdList: [],
      connectionDetails: null,
    });
    setIsValid(isFormValid);
  };

  const handleUpdateData = (key: string, value: string | string[] | null) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    setDataAccessSource(null);
  };

  const handleSaveClick = () => {
    if (!isValid && isAccessGrantedWithNoMMP) {
      return;
    }

    updateSelfServeRequestStatus({
      status: status as SelfServeDataAccessStatus,
      mmp: dataAccessSource ? (dataAccessSource as DataAccessProvider) : null,
      accountName: formData.accountName || null,
      iapMargin: formData.iapMargin || null,
      iaaIdList: formData.iaaIdList || null,
      connectionDetails: formData.connectionDetails || null,
    });
  };

  const isStatusUnchanged = requestStatus === status;
  const isStatusBeingUpdated = isUpdatingStatus;
  const isAccessGrantedWithNoMMP =
    status === SelfServeDataAccessStatus.ACCESS_GRANTED && mmp === DataAccessProvider.NONE;

  let disableSaveButton = isStatusUnchanged || isStatusBeingUpdated;
  if (isAccessGrantedWithNoMMP) {
    disableSaveButton = !isValid || disableSaveButton;
  }

  const isRequestStatusDisabled =
    (mmp !== null && !isAdminAllowedToModifyRequestStatus(mmp as DataAccessProvider)) ||
    getDropdownOptions(requestStatus || '').length <= 1;

  return (
    <Stack
      padding={'1rem'}
      gap={'1rem'}
      sx={{
        background: '#fff',
        borderRadius: '1rem',
      }}
    >
      <Typography fontSize={'1rem'} lineHeight={'1.25rem'} fontWeight={700} color={'#6C6C6C'}>
        Select Request Status
      </Typography>
      <FormControl fullWidth variant="outlined">
        <Select
          labelId="self-serve-request-status-label"
          id="self-serve-request-status"
          value={status}
          onChange={(e) => handleStatusChange(e.target.value)}
          disabled={isRequestStatusDisabled}
          MenuProps={{
            sx: {
              zIndex: 10000,
            },
          }}
        >
          {getDropdownOptions(requestStatus || '').map((option) => (
            <MenuItem key={option.value} value={option.value} sx={menuItemStyles}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {isAccessGrantedWithNoMMP && (
        <Box>
          <FormControl fullWidth required>
            <InputLabel id="data-access-source-label">Select an Account</InputLabel>
            <Select
              labelId="data-access-source-label"
              label="Select an Account"
              value={dataAccessSource}
              onChange={(e: any) => {
                setDataAccessSource(e.target.value);
              }}
              fullWidth
              sx={{
                height: '56px',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                marginBottom: '1rem',
              }}
              MenuProps={{
                sx: {
                  zIndex: 10000,
                },
              }}
            >
              {DATA_ACCESS_SOURCE_OPTIONS.map((option, index) => (
                <MenuItem
                  key={`${index}--${option.value}`}
                  value={option.value}
                  sx={{
                    ...menuItemStyles,
                  }}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DataAccessSourceFields
            dataAccessSource={dataAccessSource}
            newGameData={formData}
            handleUpdateNewGameData={handleUpdateData}
            onValidityChange={setIsValid}
          />
        </Box>
      )}

      {!isRequestStatusDisabled && requestStatus !== SelfServeDataAccessStatus.REOPEN && (
        <Box>
          <Button
            disableElevation
            disableFocusRipple
            disableRipple
            disableTouchRipple
            disabled={disableSaveButton}
            onClick={handleSaveClick}
            variant="contained"
            color="primary"
            sx={{
              borderRadius: '1.25rem',
              textTransform: 'none',
              background: '#2F736E',
              color: '#fff',
            }}
          >
            {isUpdatingStatus ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      )}
    </Stack>
  );
};

export default RequestStatusSelector;
